import React, { useState, useMemo } from 'react'
import { Search, MoreHorizontal, Copy, Trash2 } from 'lucide-react'

import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

interface EntityListProps<T> {
  items: T[]
  selectedId: string | number | undefined
  getId: (item: T) => string | number
  onSelect: (item: T) => void
  renderPrimary: (item: T) => React.ReactNode
  renderSecondary?: (item: T) => React.ReactNode
  renderIcon?: (item: T) => React.ReactNode
  onDuplicate?: (item: T) => void
  onDelete?: (item: T) => void
  getCanDelete?: (item: T) => boolean
  searchPlaceholder?: string
  searchFilter: (item: T, query: string) => boolean
  createButton?: React.ReactNode
  isLoading?: boolean
  emptyMessage?: string
}

function EntityListRow<T>({
  item,
  isSelected,
  onSelect,
  renderPrimary,
  renderSecondary,
  renderIcon,
  onDuplicate,
  onDelete,
  canDelete = true,
}: {
  item: T
  isSelected: boolean
  onSelect: () => void
  renderPrimary: (item: T) => React.ReactNode
  renderSecondary?: (item: T) => React.ReactNode
  renderIcon?: (item: T) => React.ReactNode
  onDuplicate?: () => void
  onDelete?: () => void
  canDelete?: boolean
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); } }}
      className={`w-full text-left px-3 py-2.5 border-b border-gray-100 transition-colors cursor-pointer ${
        isSelected ? 'bg-gray-100 border-l-2 border-l-gray-900' : 'hover:bg-gray-50'
      }`}
    >
      <div className="flex justify-between items-center">
        <div className="flex-1 min-w-0 flex items-center gap-2">
          {renderIcon && <span className="flex-shrink-0">{renderIcon(item)}</span>}
          <div className="min-w-0">
            <div className="font-medium text-detail truncate">{renderPrimary(item)}</div>
            {renderSecondary && renderSecondary(item)}
          </div>
        </div>
        {(onDuplicate || (onDelete && canDelete)) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="ml-2 h-6 w-6 flex-shrink-0"
                onClick={(e) => e.stopPropagation()}
                tabIndex={0}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onDuplicate && (
                <DropdownMenuItem
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation()
                    onDuplicate()
                  }}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Duplicate
                </DropdownMenuItem>
              )}
              {onDelete && (
                <DropdownMenuItem
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation()
                    onDelete()
                  }}
                  className="focus:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  )
}

export function EntityList<T>({
  items,
  selectedId,
  getId,
  onSelect,
  renderPrimary,
  renderSecondary,
  renderIcon,
  onDuplicate,
  onDelete,
  getCanDelete,
  searchPlaceholder = 'Search...',
  searchFilter,
  createButton,
  isLoading,
  emptyMessage = 'No items found',
}: EntityListProps<T>) {
  const [search, setSearch] = useState('')

  const filteredItems = useMemo(() => {
    if (!search.trim()) return items
    const q = search.toLowerCase()
    return items.filter((item) => searchFilter(item, q))
  }, [items, search, searchFilter])

  return (
    <div className="w-full lg:w-64 xl:w-72 flex-shrink-0 bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col">
      <div className="p-3 border-b border-gray-200 space-y-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-9"
          />
        </div>
        {createButton && <div>{createButton}</div>}
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="p-4 text-body text-gray-500">Loading...</div>
        ) : filteredItems.length === 0 ? (
          <div className="p-4 text-body text-gray-500">
            {search ? 'No items match your search' : emptyMessage}
          </div>
        ) : (
          filteredItems.map((item) => {
            const id = getId(item)
            return (
              <EntityListRow
                key={String(id)}
                item={item}
                isSelected={selectedId === id}
                onSelect={() => onSelect(item)}
                renderPrimary={renderPrimary}
                renderSecondary={renderSecondary}
                renderIcon={renderIcon}
                onDuplicate={onDuplicate ? () => onDuplicate(item) : undefined}
                onDelete={onDelete ? () => onDelete(item) : undefined}
                canDelete={getCanDelete ? getCanDelete(item) : true}
              />
            )
          })
        )}
      </div>
    </div>
  )
}